// Plan Items List Component
// Displays all items added to the mock plan

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Trash2, Package, Edit2, Save, X } from 'lucide-react';
import { formatPrice, getPackageConfig } from '@/lib/package-config';
import { cn } from '@/lib/utils';

interface PlanItem {
  id: string;
  careerStage: string;
  numberOfShows: number;
  numberOfArtists: number;
  showsPerArtist: number;
  artistsWithBaseShows?: number;
  artistsWithExtraShow?: number;
  packageTier: 'set-core' | 'set-audio' | 'set-video';
  pricePerShow: number;
  totalPrice: number;
}

interface PlanItemsListProps {
  planItems: PlanItem[];
  onRemoveItem: (itemId: string) => void;
  onUpdateItem: (itemId: string, updates: Partial<PlanItem>) => Promise<void>;
  onClearAll: () => void;
}

// Career stages for dropdown
const careerStages = [
  'Developing',
  'Emerging',
  'Established 1',
  'Established 2',
  'Established 3',
  'Mainstream 1',
  'Mainstream 2',
  'Mainstream 3',
  'Mainstream 4'
];

export function PlanItemsList({ planItems, onRemoveItem, onUpdateItem, onClearAll }: PlanItemsListProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Partial<PlanItem>>({});
  const [isSaving, setIsSaving] = useState(false);

  const handleEdit = (item: PlanItem) => {
    setEditingId(item.id);
    setEditValues({
      careerStage: item.careerStage,
      numberOfShows: item.numberOfShows,
      numberOfArtists: item.numberOfArtists,
      packageTier: item.packageTier,
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditValues({});
  };

  const handleSave = async (itemId: string) => {
    setIsSaving(true);
    try {
      await onUpdateItem(itemId, editValues);
      setEditingId(null);
      setEditValues({});
    } catch (error) {
      console.error('Error updating item:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (planItems.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Package className="h-12 w-12 mb-4 opacity-20" />
            <p className="text-center">
              Add shows to your plan using the calculator above
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Calculate totals
  const totalShows = planItems.reduce((sum, item) => sum + item.numberOfShows, 0);
  const totalArtists = planItems.reduce((sum, item) => sum + item.numberOfArtists, 0);
  const totalInvestment = planItems.reduce((sum, item) => sum + item.totalPrice, 0);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Plan Items
            <Badge variant="secondary" className="ml-2">
              {planItems.length} {planItems.length === 1 ? 'item' : 'items'}
            </Badge>
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={onClearAll}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Clear All
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Career Stage</TableHead>
                <TableHead className="text-center">Shows</TableHead>
                <TableHead className="text-center">% of Total</TableHead>
                <TableHead className="text-center">Artists</TableHead>
                <TableHead>Package</TableHead>
                <TableHead className="text-right">Price per Show</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {planItems.map((item) => {
                const packageConfig = getPackageConfig(item.packageTier);
                const percentOfTotal = ((item.numberOfShows / totalShows) * 100).toFixed(1);
                const isEditing = editingId === item.id;

                return (
                  <TableRow key={item.id}>
                    {/* Career Stage */}
                    <TableCell className="font-medium">
                      {isEditing ? (
                        <Select
                          value={editValues.careerStage || item.careerStage}
                          onValueChange={(value) => setEditValues({ ...editValues, careerStage: value })}
                        >
                          <SelectTrigger className="h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {careerStages.map((stage) => (
                              <SelectItem key={stage} value={stage}>
                                {stage}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        item.careerStage
                      )}
                    </TableCell>

                    {/* Shows */}
                    <TableCell className="text-center">
                      {isEditing ? (
                        <Input
                          type="number"
                          min={1}
                          value={editValues.numberOfShows ?? item.numberOfShows}
                          onChange={(e) => setEditValues({ ...editValues, numberOfShows: parseInt(e.target.value) || 1 })}
                          className="h-8 w-20 text-center"
                        />
                      ) : (
                        item.numberOfShows
                      )}
                    </TableCell>

                    {/* % of Total */}
                    <TableCell className="text-center text-sm text-gray-600">
                      {percentOfTotal}%
                    </TableCell>

                    {/* Artists */}
                    <TableCell className="text-center">
                      {isEditing ? (
                        <Input
                          type="number"
                          min={1}
                          value={editValues.numberOfArtists ?? item.numberOfArtists}
                          onChange={(e) => setEditValues({ ...editValues, numberOfArtists: parseInt(e.target.value) || 1 })}
                          className="h-8 w-20 text-center"
                        />
                      ) : (
                        item.numberOfArtists
                      )}
                    </TableCell>

                    {/* Package */}
                    <TableCell>
                      {isEditing ? (
                        <Select
                          value={editValues.packageTier || item.packageTier}
                          onValueChange={(value) => setEditValues({ ...editValues, packageTier: value as 'set-core' | 'set-audio' | 'set-video' })}
                        >
                          <SelectTrigger className="h-8 w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="set-core">SET Core</SelectItem>
                            <SelectItem value="set-audio">SET Audio</SelectItem>
                            <SelectItem value="set-video">SET Video</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Badge
                          className={cn(
                            "text-white",
                            item.packageTier === 'set-core' && "bg-gray-600",
                            item.packageTier === 'set-audio' && "bg-blue-600",
                            item.packageTier === 'set-video' && "bg-purple-600"
                          )}
                        >
                          {packageConfig.name}
                        </Badge>
                      )}
                    </TableCell>

                    {/* Price per Show */}
                    <TableCell className="text-right font-mono">
                      {formatPrice(item.pricePerShow)}
                    </TableCell>

                    {/* Total */}
                    <TableCell className="text-right font-mono font-semibold">
                      {formatPrice(item.totalPrice)}
                    </TableCell>

                    {/* Actions */}
                    <TableCell>
                      {isEditing ? (
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSave(item.id)}
                            disabled={isSaving}
                            className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                          >
                            <Save className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleCancel}
                            disabled={isSaving}
                            className="h-8 w-8 p-0 text-gray-600 hover:text-gray-700 hover:bg-gray-50"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(item)}
                            className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onRemoveItem(item.id)}
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {/* Totals Row */}
              <TableRow className="bg-gray-50 font-semibold border-t-2">
                <TableCell colSpan={1}>Total</TableCell>
                <TableCell className="text-center">{totalShows}</TableCell>
                <TableCell className="text-center text-sm">100%</TableCell>
                <TableCell className="text-center">{totalArtists}</TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell className="text-right font-mono text-lg">
                  {formatPrice(totalInvestment)}
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
